import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Segmented, InputNumber, Checkbox, Button, Spin, Steps } from 'antd';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../../config';
import { NdoeImage } from '../../components/ndoe';
import './assistantConfusion.css';

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;
const $ = (n) => `${Math.round(Number(n) || 0).toLocaleString('fr-FR')} $`;

/**
 * Assistant « confusion vendu/retourné » (demande proprio 21/07).
 *
 * Wizard reprenable en 3 étapes : (1) reconfirmer chaque paire gardée/rendue,
 * (2) l'argent (trop-perçu → remboursement cash), (3) récap + validation.
 * Le dossier est sauvé à chaque étape : fermer en cours = « dossier en cours ».
 *
 * Props : open, idCommande (ouvrir/reprendre par commande), onClose, onDone.
 */
export default function AssistantConfusion({ open, idCommande, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [dossier, setDossier] = useState(null);      // { id_dossier, etat, donnees, ... }
  const [etape, setEtape] = useState('paires');
  const [cibles, setCibles] = useState({});          // { idDl: 'garde'|'rendue' }
  const [prix, setPrix] = useState({});              // { idDl: montant } (paires gardées)
  const [rembourser, setRembourser] = useState(true);
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  // Ouverture / reprise du dossier de la commande.
  const ouvrir = useCallback(async () => {
    if (!open || !idCommande) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/regularisation/commande/${idCommande}/dossier`, {});
      setDossier(data);
      // Restaure les réponses déjà saisies (reprise) ou l'état actuel par défaut.
      const c = {}; const p = {};
      data.etat.lignes.forEach((l) => {
        c[l.id_detail_livraison] = (data.donnees?.cibles || {})[l.id_detail_livraison]
          || (l.vendue_actuellement ? 'garde' : 'rendue');
        const px = (data.donnees?.prix || {})[l.id_detail_livraison];
        p[l.id_detail_livraison] = px != null ? px : (l.prix_vente != null ? l.prix_vente : l.prix_commande);
      });
      setCibles(c); setPrix(p);
      // Reprise directe à l'étape « argent »/« récap » : le plan vient avec le
      // dossier, sinon la fenêtre serait vide.
      setPlan(data.plan || null);
      setRembourser((data.plan?.remboursement_propose || 0) > 0);
      setEtape(data.etape || 'paires');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Ouverture du dossier impossible.');
      onClose();
    } finally { setLoading(false); }
  }, [open, idCommande, onClose]);
  useEffect(() => { if (open) ouvrir(); }, [open, ouvrir]);

  const sauver = useCallback(async (nextEtape) => {
    if (!dossier) return null;
    const { data } = await axios.put(`${DOMAIN}/api/regularisation/dossier/${dossier.id_dossier}`, {
      etape: nextEtape, donnees: { cibles, prix },
    });
    setDossier(data);
    return data;
  }, [dossier, cibles, prix]);

  const allerArgent = async () => {
    setBusy(true);
    try {
      const d = await sauver('argent');
      setPlan(d.plan);
      setRembourser((d.plan?.remboursement_propose || 0) > 0);
      setEtape('argent');
    } catch (e) { toast.error(e.response?.data?.error || 'Enregistrement impossible.'); }
    finally { setBusy(false); }
  };

  const allerRecap = async () => {
    setBusy(true);
    try { await sauver('recap'); setEtape('recap'); }
    catch (e) { toast.error(e.response?.data?.error || 'Enregistrement impossible.'); }
    finally { setBusy(false); }
  };

  const valider = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${DOMAIN}/api/regularisation/dossier/${dossier.id_dossier}/resoudre`, {
        rembourser: plan && plan.trop_percu > 0 ? rembourser : false,
      });
      toast.success(data.message || 'Régularisation appliquée.');
      onDone?.(data);
    } catch (e) { toast.error(e.response?.data?.error || 'Régularisation impossible.'); }
    finally { setBusy(false); }
  };

  const abandonner = async () => {
    if (!dossier) { onClose(); return; }
    try { await axios.delete(`${DOMAIN}/api/regularisation/dossier/${dossier.id_dossier}`); } catch (e) { /* ignore */ }
    onClose();
  };

  const etapeIndex = { paires: 0, argent: 1, recap: 2 }[etape] || 0;
  const lignes = dossier?.etat?.lignes || [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={620}
      footer={null}
      destroyOnClose
      title={dossier ? `Régularisation — commande #${dossier.id_commande} · ${dossier.etat.cliente || ''}` : 'Régularisation'}
    >
      {loading || !dossier ? (
        <div className="ac-attente"><Spin /></div>
      ) : (
        <>
          <Steps size="small" current={etapeIndex} className="ac-steps" items={[
            { title: 'Les paires' }, { title: 'L’argent' }, { title: 'Valider' },
          ]} />

          {/* ── Étape 1 : reconfirmer chaque paire ─────────────────────── */}
          {etape === 'paires' && (
            <>
              <p className="ac-consigne">Pour chaque paire, dites ce qui s’est <b>réellement</b> passé — gardée par la cliente (vendue) ou rendue.</p>
              <div className="ac-lignes">
                {lignes.map((l) => (
                  <div className="ac-ligne" key={l.id_detail_livraison}>
                    <NdoeImage src={l.img} alt={l.libelle} size="sm" preview={false} />
                    <div className="ac-ligne__txt">
                      <b>{l.libelle}</b>
                      <small>Enregistré : {l.vendue_actuellement ? 'vendue' : 'rendue'}</small>
                    </div>
                    <div className="ac-ligne__choix">
                      <Segmented
                        value={cibles[l.id_detail_livraison]}
                        onChange={(v) => setCibles((c) => ({ ...c, [l.id_detail_livraison]: v }))}
                        options={[{ value: 'garde', label: 'Gardée' }, { value: 'rendue', label: 'Rendue' }]}
                      />
                      {cibles[l.id_detail_livraison] === 'garde' && (
                        <InputNumber
                          className="ac-prix" size="small" min={0} controls={false} addonAfter="$"
                          value={prix[l.id_detail_livraison]}
                          onChange={(v) => setPrix((p) => ({ ...p, [l.id_detail_livraison]: v }))}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="ac-actions">
                <Button onClick={onClose}>Fermer (reprendre plus tard)</Button>
                <Button type="primary" loading={busy} onClick={allerArgent}>Suivant →</Button>
              </div>
            </>
          )}

          {/* ── Étape 2 : l'argent ─────────────────────────────────────── */}
          {etape === 'argent' && plan && (
            <>
              <div className="ac-argent">
                <div className="ac-argent__ligne"><span>Total réel des paires gardées</span><b>{$(plan.total_corrige)}</b></div>
                <div className="ac-argent__ligne"><span>Déjà payé par la cliente</span><b>{$(plan.montant_paye)}</b></div>
                {plan.trop_percu > 0 ? (
                  <>
                    <div className="ac-argent__ecart is-rembours"><span>Trop-perçu</span><b>{$(plan.trop_percu)}</b></div>
                    <Checkbox checked={rembourser} onChange={(e) => setRembourser(e.target.checked)} className="ac-check">
                      Rembourser <b>{$(plan.trop_percu)}</b> à la cliente en espèces
                    </Checkbox>
                  </>
                ) : plan.manque > 0 ? (
                  <div className="ac-argent__ecart is-du"><span>Reste dû par la cliente</span><b>{$(plan.manque)}</b>
                    <small>La créance sera ajustée automatiquement (aucun mouvement d’espèces).</small></div>
                ) : (
                  <div className="ac-argent__ecart is-ok">Comptes équilibrés — rien à rembourser.</div>
                )}
              </div>
              <div className="ac-actions">
                <Button onClick={() => setEtape('paires')}>← Retour</Button>
                <Button type="primary" loading={busy} onClick={allerRecap}>Suivant →</Button>
              </div>
            </>
          )}

          {/* ── Étape 3 : récap + valider ──────────────────────────────── */}
          {etape === 'recap' && plan && (
            <>
              <p className="ac-consigne">Vérifiez, puis validez. Tout est écrit d’un bloc (stock, ventes, créance, remboursement) et tracé.</p>
              <div className="ac-recap">
                {plan.a_vendre.length > 0 && (
                  <p>✅ <b>Vendues</b> : {plan.a_vendre.map((l) => `${l.libelle} (${$(l.prix)})`).join(', ')}</p>
                )}
                {plan.a_rendre.length > 0 && (
                  <p>↩️ <b>Rendues</b> (ventes annulées, stock rendu) : {plan.a_rendre.map((l) => l.libelle).join(', ')}</p>
                )}
                {plan.trop_percu > 0 && rembourser && (
                  <p>💵 <b>Remboursement cash</b> : {$(plan.trop_percu)}</p>
                )}
                {plan.a_vendre.length === 0 && plan.a_rendre.length === 0 && (
                  <p className="ac-rien">Aucun changement : chaque paire est déjà dans l’état confirmé.</p>
                )}
              </div>
              <div className="ac-actions">
                <Button onClick={() => setEtape('argent')}>← Retour</Button>
                <Button danger onClick={abandonner}>Abandonner le dossier</Button>
                <Button type="primary" loading={busy}
                  disabled={plan.a_vendre.length === 0 && plan.a_rendre.length === 0}
                  onClick={valider}>Valider la régularisation</Button>
              </div>
            </>
          )}
        </>
      )}
    </Modal>
  );
}
